import { useState, useCallback, useEffect, useRef } from "react";
import { PaginationParams, PageResponse } from "../types";

interface UsePaginationOptions<T> {
  fetchFn: (params: PaginationParams) => Promise<PageResponse<T>>;
  initialPage?: number;
  initialSize?: number;
  initialSortBy?: string;
  initialSortDirection?: "asc" | "desc";
  initialSearch?: string;
  debounceMs?: number;
  enabled?: boolean;
  /** Key that when changed, resets pagination state and triggers a refetch (e.g., lotId) */
  resetKey?: string;
}

interface UsePaginationResult<T> {
  // Data
  items: T[];
  totalElements: number;
  totalPages: number;

  // Pagination state
  page: number;
  size: number;
  sortBy: string | undefined;
  sortDirection: "asc" | "desc";
  search: string;

  // Status
  loading: boolean;
  error: string | null;

  // Actions
  goToPage: (page: number) => void;
  goToFirstPage: () => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  goToLastPage: () => void;
  setPageSize: (size: number) => void;
  setSort: (field: string, direction?: "asc" | "desc") => void;
  setSearch: (query: string) => void;
  refresh: () => void;
}

export function usePagination<T>({
  fetchFn,
  initialPage = 0,
  initialSize = 10,
  initialSortBy,
  initialSortDirection = "desc",
  initialSearch = "",
  debounceMs = 300,
  enabled = true,
  resetKey,
}: UsePaginationOptions<T>): UsePaginationResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(initialPage);
  const [size, setSize] = useState(initialSize);
  const [sortBy, setSortBy] = useState<string | undefined>(initialSortBy);
  const [sortDirection, setSortDirection] =
    useState<"asc" | "desc">(initialSortDirection);
  const [searchState, setSearchState] = useState(initialSearch);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Counter to force refetch when resetKey changes
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchFnRef = useRef(fetchFn);
  const isMountedRef = useRef(true);

  // Track mount status and cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clean up debounce timer on unmount
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Update fetchFn ref when it changes
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  // Track the previous resetKey to detect changes
  const prevResetKeyRef = useRef(resetKey);

  // Reset state and refetch when resetKey changes
  useEffect(() => {
    if (prevResetKeyRef.current !== resetKey && prevResetKeyRef.current !== undefined) {
      // Reset all pagination state to initial values
      setPage(initialPage);
      setSize(initialSize);
      setSortBy(initialSortBy);
      setSortDirection(initialSortDirection);
      setSearchState(initialSearch);
      setItems([]);
      setTotalElements(0);
      setTotalPages(0);
      setError(null);
      // Increment trigger to force a refetch with the new fetchFn
      setFetchTrigger(prev => prev + 1);
    }
    prevResetKeyRef.current = resetKey;
  }, [resetKey, initialPage, initialSize, initialSortBy, initialSortDirection, initialSearch]);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const params: PaginationParams = {
        page,
        size,
        sortBy,
        sortDirection,
        search: searchState || undefined,
      };

      const response = await fetchFnRef.current(params);

      // Guard against state updates on unmounted component
      if (!isMountedRef.current) return;

      setItems(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (err: unknown) {
      // Guard against state updates on unmounted component
      if (!isMountedRef.current) return;

      // Check for abort errors (different libraries may use different names)
      const isAbortError = err instanceof Error && (
        err.name === "AbortError" ||
        err.name === "CanceledError" ||
        (err as any).code === "ERR_CANCELED"
      );

      if (!isAbortError) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // Include fetchTrigger to force refetch when resetKey changes
  }, [enabled, page, size, sortBy, sortDirection, searchState, fetchTrigger]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  // Debounced search
  const setSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setSearchState(query);
        setPage(0); // Reset to first page on search
      }, debounceMs);
    },
    [debounceMs]
  );

  // Navigation helpers
  const goToPage = useCallback(
    (targetPage: number) => {
      const maxPage = Math.max(0, totalPages - 1);
      setPage(Math.max(0, Math.min(targetPage, maxPage)));
    },
    [totalPages]
  );

  const goToFirstPage = useCallback(() => setPage(0), []);

  const goToPreviousPage = useCallback(() => {
    setPage((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  const goToLastPage = useCallback(() => {
    setPage(Math.max(0, totalPages - 1));
  }, [totalPages]);

  const setPageSize = useCallback((newSize: number) => {
    setSize(newSize);
    setPage(0); // Reset to first page when changing page size
  }, []);

  const setSort = useCallback(
    (field: string, direction?: "asc" | "desc") => {
      if (field === sortBy && !direction) {
        // Toggle direction if same field
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDirection(direction || "asc");
      }
      setPage(0); // Reset to first page on sort change
    },
    [sortBy]
  );

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    items,
    totalElements,
    totalPages,
    page,
    size,
    sortBy,
    sortDirection,
    search: searchState,
    loading,
    error,
    goToPage,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  };
}
